import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { ImageDetailData } from '../types/index.ts';
import { useImageDetail } from '../hooks/useImageDetail.ts';
import { useKeyboardNavigation } from '../hooks/useKeyboardNavigation.ts';
import { useDelayedLoading } from '../hooks/useDelayedLoading.ts';
import { useSwipeGestures } from '../hooks/useSwipeGestures.ts';
import { useSwipeDrag } from '../hooks/useSwipeDrag.ts';
import { useImagePreload } from '../hooks/useImagePreload.ts';
import { ImageDisplay } from '../components/ImageDetail/ImageDisplay.tsx';
import { ImageNavigation } from '../components/ImageDetail/ImageNavigation.tsx';
import { MobileNavigation } from '../components/ImageDetail/MobileNavigation.tsx';
import { VersionPicker } from '../components/ImageDetail/VersionPicker.tsx';
import { ImageMetadata, CameraMetadata, LocationMetadata, AIMetadata } from '../components/ImageDetail/ImageMetadata.tsx';
import { AstroSkyMap } from '../components/ImageDetail/AstroSkyMap.tsx';
import {
  AstroControls,
  AstroOverlay,
  AstroQuickToggle,
  DEFAULT_LABEL_DENSITY,
  useAstroSolution,
} from '../components/ImageDetail/AstroOverlay.tsx';
import { UserMetadata } from '../components/ImageDetail/UserMetadata.tsx';
import { ImageControls } from '../components/ImageDetail/ImageControls.tsx';
import { MobileTray, TrayThumbnails } from '../components/ImageDetail/MobileTray.tsx';
import { useMediaQuery } from '../hooks/useMediaQuery.ts';
import { formatHours } from '../utils/format-hours.ts';
import { EditModal } from '../components/Editor/index.ts';
import { contentEditorApi } from '../api/content-editor.ts';

interface ImageDetailPageProps {
  initialData: ImageDetailData;
  galleryUrl: string;
  hideMetadata?: boolean;
  isAuthenticated?: boolean;
  currentUser?: string;
}

interface Breadcrumb {
  path: string;
  display_name: string;
  is_current: boolean;
}

function createMountErrorFallback(): HTMLElement {
  const fallback = document.createElement('div');
  Object.assign(fallback.style, {
    padding: '2rem',
    textAlign: 'center',
    border: '2px solid #dc3545',
    background: '#f8d7da',
    color: '#721c24',
    borderRadius: '4px',
  });

  const title = document.createElement('h3');
  title.textContent = 'React Enhancement Failed';

  const message = document.createElement('p');
  message.textContent = 'The image detail page could not be loaded properly.';

  const reloadButton = document.createElement('button');
  reloadButton.type = 'button';
  reloadButton.textContent = 'Reload Page';
  reloadButton.addEventListener('click', () => window.location.reload());

  fallback.append(title, message, reloadButton);
  return fallback;
}

/** One short line for the phone tray: integration and optics for astro
 * images, otherwise the capture date and camera */
function imageSummary(image: ImageDetailData['image']): string {
  const camera = image.camera_info;
  const parts: string[] = [];
  if (camera?.total_exposure_time) parts.push(formatHours(camera.total_exposure_time));
  if (camera?.telescope) {
    parts.push(camera.telescope);
  } else {
    if (image.capture_date) parts.push(image.capture_date.replace(/ at .*$/, ''));
    if (camera?.camera_model) parts.push(camera.camera_model);
  }
  return parts.join(' · ');
}

function Breadcrumbs({ breadcrumbs, galleryUrl, currentImageTitle, imagePath }: {
  breadcrumbs: Breadcrumb[] | any;
  galleryUrl: string;
  currentImageTitle: string;
  imagePath: string;
}) {
  // Handle case where breadcrumbs might not be an array
  const safeBreadcrumbs = Array.isArray(breadcrumbs) ? breadcrumbs : [];
  const lastCrumb = safeBreadcrumbs[safeBreadcrumbs.length - 1];
  const showCurrent = !lastCrumb || lastCrumb.display_name !== currentImageTitle;

  return (
    <nav className="gallery-nav">
      {safeBreadcrumbs.map((crumb, index) => {
        const isLast = index === safeBreadcrumbs.length - 1;
        // Add anchor to the last breadcrumb (current folder) to scroll to this image
        const anchor = isLast ? `#${imagePath}` : '';
        const href = `${galleryUrl}${crumb.path ? `/${crumb.path}` : ''}${anchor}`;

        return (
          <React.Fragment key={index}>
            {index > 0 && <span className="nav-separator">→</span>}
            <a href={href} className="nav-link">{crumb.display_name}</a>
          </React.Fragment>
        );
      })}
      {showCurrent && (
        <>
          <span className="nav-separator">→</span>
          <span className="nav-current">{currentImageTitle}</span>
        </>
      )}
    </nav>
  );
}

export function ImageDetailPage({ 
  initialData, 
  galleryUrl, 
  hideMetadata = false,
  isAuthenticated = false,
  currentUser
}: ImageDetailPageProps) {
  const { data: imageData, loading, error, loadImage, updateMetadata } = useImageDetail({
    galleryName: initialData.gallery_name,
    initialData
  });
  
  // Use initialData immediately if no other data is available
  const currentData = imageData || initialData;

  // The folder this image belongs to, scrolled to this image
  const folderCrumb = Array.isArray(currentData?.breadcrumbs)
    ? currentData.breadcrumbs[currentData.breadcrumbs.length - 1]
    : undefined;
  const folderName = folderCrumb?.display_name || 'Gallery';
  const folderHref = `${galleryUrl}${folderCrumb?.path ? `/${folderCrumb.path}` : ''}#${currentData?.image.path || ''}`;
  
  // Only show loading after 500ms delay
  const showLoading = useDelayedLoading(loading && !currentData);
  
  // Ref for swipe gestures
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // Track zoom state to disable swipe navigation when zoomed
  const [isImageZoomed, setIsImageZoomed] = useState(false);

  // Phones (and phones held sideways) get a fixed layout: a slim top bar, an
  // image that fits the screen, and a bottom tray with everything else
  const isPhone = useMediaQuery('(max-width: 768px), (pointer: coarse) and (max-height: 500px)');
  const [trayExpanded, setTrayExpanded] = useState(false);
  const [immersive, setImmersive] = useState(false);
  const [siteMenuOpen, setSiteMenuOpen] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageBox, setStageBox] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    if (!isPhone) return;
    document.body.classList.add('image-detail-phone');
    return () => document.body.classList.remove('image-detail-phone');
  }, [isPhone]);

  useEffect(() => {
    document.body.classList.toggle('image-detail-phone-menu', isPhone && siteMenuOpen);
    if (!isPhone || !siteMenuOpen) return;
    // Capture phase on document, so Escape closes the menu before keyboard
    // navigation (which treats Escape as "back to the folder") sees it
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopImmediatePropagation();
      setSiteMenuOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.body.classList.remove('image-detail-phone-menu');
      document.removeEventListener('keydown', onKey, true);
    };
  }, [isPhone, siteMenuOpen]);

  // Moving to another image, or leaving the phone layout, closes the menu
  useEffect(() => {
    setSiteMenuOpen(false);
  }, [currentData?.image.path, isPhone]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!isPhone || !stage) return;
    const measure = () => {
      const rect = stage.getBoundingClientRect();
      setStageBox((prev) =>
        prev && prev.width === rect.width && prev.height === rect.height
          ? prev
          : { width: rect.width, height: rect.height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [isPhone]);

  // Plate solution for astro images (null when unsolved or unavailable).
  // Overlay state lives here so the zoomed views share it.
  const astroSolution = useAstroSolution(
    currentData?.gallery_name || '',
    currentData?.image?.path || '',
  );
  const [astroVisible, setAstroVisible] = useState(false);
  const [astroAllTransients, setAstroAllTransients] = useState(false);
  const [astroHiddenGroups, setAstroHiddenGroupsState] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem('astro-hidden-catalogs') || '[]');
    } catch {
      return [];
    }
  });
  const setAstroHiddenGroups = (groups: string[]) => {
    setAstroHiddenGroupsState(groups);
    try {
      localStorage.setItem('astro-hidden-catalogs', JSON.stringify(groups));
    } catch {
      /* private mode */
    }
  };
  const [astroDensity, setAstroDensityState] = useState<number>(() => {
    const stored = Number(localStorage.getItem('astro-label-density'));
    return Number.isFinite(stored) && stored > 0 ? stored : DEFAULT_LABEL_DENSITY;
  });
  const setAstroDensity = (density: number) => {
    setAstroDensityState(density);
    try {
      localStorage.setItem('astro-label-density', String(density));
    } catch {
      /* private mode */
    }
  };
  const [astroOutlines, setAstroOutlinesState] = useState<boolean>(
    () => localStorage.getItem('astro-precise-outlines') !== 'false',
  );
  const setAstroOutlines = (outlines: boolean) => {
    setAstroOutlinesState(outlines);
    try {
      localStorage.setItem('astro-precise-outlines', String(outlines));
    } catch {
      /* private mode */
    }
  };

  // Modal state for editing image info
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Handler for saving image title and description
  const handleSaveImageInfo = useCallback(async (title: string, markdown: string) => {
    await contentEditorApi.updateImageDescription(
      currentData.gallery_name,
      currentData.image.path,
      markdown,
      title || undefined
    );
    // Reload image data to get updated info
    await loadImage(currentData.image.path);
  }, [currentData?.gallery_name, currentData?.image.path, loadImage]);

  // Navigate to a specific image by its navigation data
  const handleNavigateToImage = async (image: { path: string; name: string }) => {
    try {
      // Load new image data
      await loadImage(image.path);

      // Update URL without page reload
      const newUrl = `${galleryUrl}/detail/${image.path}`;
      window.history.pushState({}, '', newUrl);

      // Update document title
      document.title = `${image.name} - theatr.us`;

    } catch (err) {
      console.error(`Failed to navigate to image:`, err);
    }
  };

  // Enhanced navigation with SPA-style URL updates
  const handleNavigation = async (direction: 'prev' | 'next') => {
    if (!currentData) return;

    const targetImage = direction === 'prev' ? currentData.prev_image : currentData.next_image;
    if (!targetImage) return;

    await handleNavigateToImage(targetImage);
  };

  useKeyboardNavigation({
    prevImage: currentData?.prev_image,
    nextImage: currentData?.next_image,
    galleryUrl,
    imagePath: currentData?.image.path || '',
    onNavigate: (direction) => {
      if (direction === 'back') {
        window.location.href = folderHref;
      } else {
        handleNavigation(direction);
      }
    }
  });

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const detailPrefix = `${galleryUrl}/detail/`;
      const path = window.location.pathname;
      if (path.startsWith(detailPrefix)) {
        const imagePath = decodeURIComponent(path.slice(detailPrefix.length));
        if (imagePath && imagePath !== currentData?.image.path) {
          loadImage(imagePath);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [galleryUrl, loadImage, currentData?.image.path]);

  // Preload the next two images each way for faster navigation
  useImagePreload([
    currentData?.next_images?.[0],
    currentData?.prev_images?.[0],
    currentData?.next_images?.[1],
    currentData?.prev_images?.[1],
  ]);


  // Tablets and larger touch screens: navigate on a completed swipe
  useSwipeGestures(imageContainerRef, {
    onSwipeLeft: () => {
      if (currentData?.next_image) {
        handleNavigation('next');
      }
    },
    onSwipeRight: () => {
      if (currentData?.prev_image) {
        handleNavigation('prev');
      }
    }
  }, { disabled: isImageZoomed || isPhone });

  // Phones: the image follows the finger, so it is clear a swipe navigates
  useSwipeDrag(imageContainerRef, {
    canPrev: !!currentData?.prev_image,
    canNext: !!currentData?.next_image,
    onPrev: () => handleNavigation('prev'),
    onNext: () => handleNavigation('next'),
    prevUrl: currentData?.prev_image?.thumbnail_url.replace(/\/thumbnail$/, '/medium'),
    nextUrl: currentData?.next_image?.thumbnail_url.replace(/\/thumbnail$/, '/medium'),
    onTap: () => {
      if (trayExpanded) setTrayExpanded(false);
      else setImmersive(!immersive);
    },
    onSwipeDown: () => {
      if (trayExpanded) {
        setTrayExpanded(false);
        return true;
      }
      window.location.href = folderHref;
    },
    disabled: !isPhone || isImageZoomed,
    resetKey: currentData?.image.path,
  });

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <h2>Error Loading Image</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Try Again</button>
      </div>
    );
  }

  if (showLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading image...</p>
      </div>
    );
  }

  const breadcrumbs = (
    <Breadcrumbs
      breadcrumbs={currentData.breadcrumbs}
      galleryUrl={galleryUrl}
      currentImageTitle={currentData.image.title || currentData.image.name}
      imagePath={currentData.image.path}
    />
  );

  const imageDisplay = (
    <ImageDisplay
      image={currentData.image}
      canUseZoom={currentData.permissions.can_use_zoom}
      canSeeAiAltText={currentData.permissions.can_see_ai_alt_text}
      tileConfig={currentData.tile_config}
      galleryName={currentData.gallery_name}
      onZoomStateChange={setIsImageZoomed}
      fitBox={isPhone ? stageBox : undefined}
      overlay={
        astroSolution ? (
          <AstroOverlay
            solution={astroSolution}
            visible={astroVisible}
            allTransients={astroAllTransients}
            hiddenGroups={astroHiddenGroups}
            density={astroDensity}
            preciseOutlines={astroOutlines}
          />
        ) : undefined
      }
      zoomOverlay={
        astroSolution && astroVisible ? (
          <AstroOverlay
            solution={astroSolution}
            visible
            allTransients={astroAllTransients}
            hiddenGroups={astroHiddenGroups}
            density={astroDensity}
            preciseOutlines={astroOutlines}
          />
        ) : undefined
      }
    />
  );

  const astroControls = astroSolution && (
    <AstroControls
      solution={astroSolution}
      visible={astroVisible}
      onVisibleChange={setAstroVisible}
      allTransients={astroAllTransients}
      onAllTransientsChange={setAstroAllTransients}
      hiddenGroups={astroHiddenGroups}
      onHiddenGroupsChange={setAstroHiddenGroups}
      density={astroDensity}
      onDensityChange={setAstroDensity}
      preciseOutlines={astroOutlines}
      onPreciseOutlinesChange={setAstroOutlines}
    />
  );

  const versionPicker = currentData.image.versions && currentData.image.versions.length > 0 && (
    <VersionPicker
      versions={currentData.image.versions}
      currentPath={currentData.image.path}
      galleryUrl={galleryUrl}
    />
  );

  const hiddenBadge = currentData.is_hidden && (
    <div className="image-hidden-badge" title="This image is hidden from users without permission">
      <span className="hidden-icon">HIDDEN</span>
    </div>
  );

  const editIcon = currentData.permissions.can_edit_content && (
    <button
      type="button"
      className="image-edit-icon"
      onClick={() => setIsEditModalOpen(true)}
      title="Edit image info"
      aria-label="Edit image info"
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
        <path d="M12.146.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1 0 .708l-10 10a.5.5 0 0 1-.168.11l-5 2a.5.5 0 0 1-.65-.65l2-5a.5.5 0 0 1 .11-.168l10-10zM11.207 2.5 13.5 4.793 14.793 3.5 12.5 1.207 11.207 2.5zm1.586 3L10.5 3.207 4 9.707V10h.5a.5.5 0 0 1 .5.5v.5h.5a.5.5 0 0 1 .5.5v.5h.293l6.5-6.5zm-9.761 5.175-.106.106-1.528 3.821 3.821-1.528.106-.106A.5.5 0 0 1 5 12.5V12h-.5a.5.5 0 0 1-.5-.5V11h-.5a.5.5 0 0 1-.468-.325z"/>
      </svg>
    </button>
  );

  const description = currentData.image.description && (
    <div
      className="image-description"
      dangerouslySetInnerHTML={{ __html: currentData.image.description }}
    />
  );

  const editModal = (
    <EditModal
      isOpen={isEditModalOpen}
      modalTitle="Edit Image"
      title={currentData.image.title || ''}
      markdownContent={currentData.image.user_metadata?.description || ''}
      descriptionPlaceholder="Add image description..."
      onSave={handleSaveImageInfo}
      onClose={() => setIsEditModalOpen(false)}
    />
  );

  const details = (
    <>
      {!hideMetadata && (
        <div className="metadata-grid">
          <ImageMetadata image={currentData.image} hideMetadata={hideMetadata} permissions={currentData.permissions} />
          <CameraMetadata image={currentData.image} permissions={currentData.permissions} />
          <AstroSkyMap image={currentData.image} permissions={currentData.permissions} />
          <LocationMetadata image={currentData.image} permissions={currentData.permissions} />
        </div>
      )}

      <AIMetadata image={currentData.image} permissions={currentData.permissions} />

      <ImageControls image={currentData.image} permissions={currentData.permissions} onEditClick={() => setIsEditModalOpen(true)} shareUrl={currentData.share_url} baseUrl={currentData.base_url} />

      {currentData.permissions.can_read_metadata && (
        <UserMetadata
          metadata={currentData.image.user_metadata}
          imagePath={currentData.image.path}
          galleryName={currentData.gallery_name}
          isAuthenticated={isAuthenticated}
          currentUser={currentUser}
          permissions={currentData.permissions}
          onUpdate={(updatedMetadata) => updateMetadata(updatedMetadata)}
          image={{
            medium_url: currentData.image.medium_url,
            dimensions: currentData.image.dimensions
          }}
        />
      )}
    </>
  );

  if (isPhone) {
    const title = currentData.image.title || currentData.image.name;
    const strip = (
      <TrayThumbnails
        current={{
          path: currentData.image.path,
          name: currentData.image.name,
          thumbnail_url: currentData.image.thumbnail_url,
        }}
        prevImages={currentData.prev_images || []}
        nextImages={currentData.next_images || []}
        onSelect={handleNavigateToImage}
      />
    );
    const hasStrip = (currentData.prev_images?.length || 0) + (currentData.next_images?.length || 0) > 0;
    const summary = imageSummary(currentData.image);

    // Portaled to <body>: iOS Safari gives the page container a transform,
    // which would pin this fixed layout to that container instead of the screen
    return createPortal(
      <div className={`phone-detail${immersive ? ' immersive' : ''}`}>
        <div className="phone-topbar">
          <a className="phone-topbar-back" href={folderHref} aria-label={`Back to ${folderName}`}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15,18 9,12 15,6" />
            </svg>
            <span>{folderName}</span>
          </a>
          <button
            type="button"
            className="phone-topbar-menu"
            aria-label="Site menu"
            aria-expanded={siteMenuOpen}
            onClick={() => setSiteMenuOpen(!siteMenuOpen)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <line x1="4" y1="7" x2="20" y2="7" />
              <line x1="4" y1="12" x2="20" y2="12" />
              <line x1="4" y1="17" x2="20" y2="17" />
            </svg>
          </button>
        </div>
        {siteMenuOpen && (
          <div className="phone-menu-backdrop" onClick={() => setSiteMenuOpen(false)} aria-hidden="true" />
        )}
        <div ref={stageRef} className="phone-stage">
          <div ref={imageContainerRef} className="swipeable-image-area">
            {imageDisplay}
          </div>
        </div>
        <MobileTray
          title={title}
          summary={summary}
          expanded={trayExpanded}
          onExpandedChange={setTrayExpanded}
          quickActions={
            astroSolution && (
              <AstroQuickToggle
                solution={astroSolution}
                visible={astroVisible}
                onVisibleChange={setAstroVisible}
                hiddenGroups={astroHiddenGroups}
                allTransients={astroAllTransients}
                density={astroDensity}
                className={`phone-tray-chip${astroVisible ? ' active' : ''}`}
              />
            )
          }
          strip={hasStrip ? strip : versionPicker}
        >
          {astroControls}
          {hiddenBadge}
          {(description || editIcon) && (
            <div className="image-header-mobile">
              {editIcon && <div className="image-title-row">{editIcon}</div>}
              {description}
            </div>
          )}
          {hasStrip && versionPicker}
          {details}
        </MobileTray>
        {editModal}
      </div>,
      document.body,
    );
  }

  return (
    <>
      {breadcrumbs}

      <div className="image-detail-content">
        {/* Image viewer section - full viewport */}
        <div className="image-viewer-section">
          <MobileNavigation
            prevImage={currentData.prev_image}
            nextImage={currentData.next_image}
            onNavigate={handleNavigation}
          />

          <div className="image-container-wrapper">
            <div ref={imageContainerRef} className="swipeable-image-area">
              {imageDisplay}
            </div>
          </div>

          {/* Astro overlay toggles sit right under the image they affect */}
          {astroControls}

          {/* Thumbnail navigation */}
          <ImageNavigation
            prevImage={currentData.prev_image}
            nextImage={currentData.next_image}
            prevImages={currentData.prev_images || []}
            nextImages={currentData.next_images || []}
            galleryUrl={galleryUrl}
            onNavigate={handleNavigation}
            onNavigateToImage={handleNavigateToImage}
            title={currentData.image.title}
            canEditContent={currentData.permissions.can_edit_content}
            onEditClick={() => setIsEditModalOpen(true)}
          />

          {/* Version picker - shows previous versions if available */}
          {versionPicker}

          {(currentData.prev_image || currentData.next_image) && (
            <div className="nav-hint">
              {currentData.prev_image && currentData.next_image ? (
                <>Use ← → keys to navigate between images</>
              ) : currentData.prev_image ? (
                <>Use ← key to go to previous image</>
              ) : (
                <>Use → key to go to next image</>
              )}
            </div>
          )}
        </div>

        {/* Image description - shown when description exists */}
        {currentData.image.description && (
          <div className="image-description-section hide-mobile">
            <div
              className="image-description-content"
              dangerouslySetInnerHTML={{ __html: currentData.image.description }}
            />
          </div>
        )}

        {/* Info section - below the image viewer */}
        <div className="image-info-section">
          {hiddenBadge}

          <div className="image-header-mobile">
            {(currentData.image.title || currentData.permissions.can_edit_content) && (
              <div className="image-title-row">
                {currentData.image.title ? (
                  <h1 className="image-title">{currentData.image.title}</h1>
                ) : currentData.permissions.can_edit_content ? (
                  <span className="image-title-placeholder">Untitled image</span>
                ) : null}
                {editIcon}
              </div>
            )}
            {description}
          </div>

          {editModal}

          {details}
        </div>
      </div>
    </>
  );
}

// Mount React component on server-rendered page
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('react-image-detail');
  if (!container) return;

  try {
    // Extract embedded JSON data from script tag
    const dataScript = document.getElementById('image-detail-data');
    if (!dataScript?.textContent) {
      throw new Error('No image detail data found');
    }

    const initialData: ImageDetailData = JSON.parse(dataScript.textContent);
    const galleryUrl = container.getAttribute('data-gallery-url') || '/gallery';
    const hideMetadata = container.getAttribute('data-hide-metadata') === 'true';
    const isAuthenticated = container.getAttribute('data-authenticated') === 'true';
    const currentUser = container.getAttribute('data-current-user') || undefined;
    
    console.log('React taking over image detail page with data:', {
      imagePath: initialData.image.path,
      imageUrl: initialData.image.medium_url,
      breadcrumbs: initialData.breadcrumbs,
      galleryUrl,
      hideMetadata,
      isAuthenticated,
      permissions: initialData.permissions
    });

    const root = createRoot(container);
    root.render(
      <ImageDetailPage 
        initialData={initialData}
        galleryUrl={galleryUrl}
        hideMetadata={hideMetadata}
        isAuthenticated={isAuthenticated}
        currentUser={currentUser}
      />
    );
  } catch (error) {
    console.error('Failed to mount React image detail component:', error);
    container.replaceChildren(createMountErrorFallback());
  }
});
